import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) return toast.error("Password must be at least 6 characters");
    if (pw !== confirm) return toast.error("Passwords do not match");
    if (!token) return toast.error("Reset token is missing from the link");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      toast.success("Password updated", { description: "Sign in with your new password." });
      nav("/login", { replace: true });
    } catch (err) {
      toast.error("Reset failed", { description: formatApiErrorDetail(err.response?.data?.detail) || err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-start justify-start px-6 md:px-16 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 mb-10">
          <div className="h-9 w-9 rounded-md bg-teal-700 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-white" />
          </div>
          <div>
            <div className="font-heading text-lg font-bold tracking-tight">Audentra</div>
            <div className="text-[0.65rem] tracking-[0.2em] uppercase text-slate-500">Compliance Hub</div>
          </div>
        </Link>

        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">Set a new password</h1>
        <p className="text-slate-600 mt-2">Choose something you&apos;ll remember. Minimum 6 characters.</p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="reset-form">
          <div className="space-y-1.5">
            <Label htmlFor="pw" className="label-caps">New password</Label>
            <Input
              id="pw"
              type="password"
              required
              minLength={6}
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              data-testid="reset-password"
              placeholder="••••••••"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm" className="label-caps">Confirm password</Label>
            <Input
              id="confirm"
              type="password"
              required
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm"
              placeholder="••••••••"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            data-testid="reset-submit"
            className="w-full h-11 bg-teal-700 hover:bg-teal-800 rounded-full"
          >
            {busy ? "Updating…" : "Update password"}
          </Button>
        </form>

        <Link
          to="/login"
          data-testid="link-back-to-login"
          className="mt-8 inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
        >
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
